import { Button, ButtonVariantEnum, IColumn, LoadingIndicator, TableList, TableListFilter, TextInput } from '@Wonder-Cave/ui';
import { useCache } from '@gr/portal/contexts/CacheContext';
import { convertEnumToReadableString } from '@gr/portal/providers/utility.provider';
import { CampaignStatusEnum, CampaignTypeEnum, EntityName, MessageTypeEnum, SocketEventNameEnum, UiCampaignStatusEnum } from '@gr/shared/enums';
import {
  Auth0Role,
  FilterDataTypeEnum,
  FilterOperatorEnum,
  ICampaignCloneRequest,
  ICampaignCreationRequest,
  ICampaignDetails,
  ICampaignsListView,
  IDeleteOneRequest,
  IExportConversationsRequest,
  IHttpResponse,
  IProvidersChangeForTypeRequest,
  ISearchFilter
} from '@gr/shared/models';
import { isCampaignPausable } from '@gr/shared/utils';
import { CheckCircleIcon, ClockIcon, MinusCircleIcon, XCircleIcon } from '@heroicons/react/solid';
import { useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { axiosPatch, axiosPost } from '../../authAxios';
import { NotificationType, useNotifications } from '../../contexts/NotificationContext';
import { SocketContext } from '../../contexts/SocketContext';
import useCampaigns from '../../hooks/useCampaigns';
import useProviderSwapWarning from '../../hooks/useProviderSwapWarning';
import useRoles from '../../hooks/useRoles';
import { IDropdownValue } from '../shared/Form/Dropdown';
import ClientDropdown from '../shared/Form/Dropdowns/ClientDropdown';
import { IRadioButtonOption } from '../shared/Form/RadioButton';
import ProviderSwapWarningModal from '../shared/ProviderSwapWarningModal';
import CampaignDeleteModal from './CampaignDeleteModal';
import CampaignPauseResumeModal from './CampaignPauseResumeModal';
import CampaignPriorityModal from './CampaignPriorityModal';
import CampaignSwapProviderModal from './CampaignSwapProviderModal';
import CampaignDeliveredProgressBar from './Columns/CampaignDeliveredProgressBar';
import CampaignStatus from './Columns/CampaignStatus';
import { CampaignTitle } from './Columns/CampaignTitle';
import { executeProviderSwap, getCampaignEndDateForClone, getCampaignEndDateForStartDate, getCampaignStartDate, getProviderSwapErrorContent, mapToStatusFilter, ProviderSwapOutcomeEnum } from './services/campaigns.service';
import { ICampaignForm, defaultCampaignsTableOptions, getColumns } from './types';

// GOTV is an initialism, so the generic enum-to-sentence conversion mangles it.
const getCampaignTypeLabel = (type: CampaignTypeEnum): string =>
  type === CampaignTypeEnum.GOTV ? 'GOTV' : convertEnumToReadableString(type);

const Campaigns = () => {
  const [showDetailsPanel, setShowDetailsPanel] = useState(false);
  const [campaignCreating, setCampaignCreating] = useState(false);
  const [defaultFormValues, setDefaultFormValues] = useState<ICampaignDetails>();
  const [searchText, setSearchText] = useState('');
  const [searchTextEnter, setSearchTextEnter] = useState('');
  const { addNotification } = useNotifications();
  const [tableOptions, setTableOptions] = useState(defaultCampaignsTableOptions);
  const socketManager = useContext(SocketContext);
  // TODO: Remove these and use hooks
  const [actionLoading, setActionLoading] = useState(false);
  const [loadingOverride, setLoadingOverride] = useState(false);
  const [selectedItem, setSelectedItem] = useState<ICampaignsListView>();
  const [showPauseModal, setShowPauseModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showPriorityModal, setShowPriorityModal] = useState(false);
  const [showSwapProviderModal, setShowSwapProviderModal] = useState(false);
  // Area-code warning gate (MES-2614) — see useProviderSwapWarning.
  const {
    showSwapWarningModal,
    confirmSwapWarning,
    handleSwapWarningConfirm,
    handleSwapWarningCancel,
    closeSwapWarningModal,
  } = useProviderSwapWarning();
  const history = useHistory();
  const { sendingWindow } = useCache();
  const isAdmin = useRoles([Auth0Role.GR_ADMIN]);

  // I'm wondering if caching won't work until we have separate API calls so that hooks know what's going on.
  // We also need to enable it in app.tsx I'm assuming
  const [{ data: campaignData, loading: campaignLoading, error: campaignError }, refetch] = useCampaigns(tableOptions);
  //const [{ data: clientsData, loading: clientsLoading, error: clientsError }, refetchClients] = useClients(allEnabledClientsSearchOptions);

  const campaigns: ICampaignsListView[] =
    campaignData?.data.records.map((campaign, idx) => ({
      ...campaign,
      status: CampaignStatusEnum[campaign.status],
    })) ?? [];

  const campaignTotalCount = campaignData?.data.totalCount;

  const handleRefetch = async () => {
    try {
      await refetch();
    } catch (err) { }
  };

  const updateCampaignsInvisible = async () => {
    // Only refresh the grid if users aren't actively filtering and they're on page one
    if (tableOptions.filters.length === 0 && tableOptions.pagination.skip === 0) {
      setLoadingOverride(true);
      await handleRefetch();
      setLoadingOverride(false);
    }
  };

  useEffect(() => {
    const searchText = tableOptions.filters.find((filter) => filter.fieldName === 'name')?.value;

    handleRefetch();

    setSearchText(searchText ?? '');
  }, [tableOptions]);

  useEffect(() => {
    //const unsubscribeTestMessage = socketManager!.subscribe(SocketEventNameEnum.TEST_MESSAGE_INBOUND, updateCampaignsInvisible);
    const unsubscribeScheduledMessages = socketManager!.subscribe(
      SocketEventNameEnum.CAMPAIGN_ALL_MESSAGES_SCHEDULED,
      updateCampaignsInvisible
    );

    return () => {
      //unsubscribeTestMessage();
      unsubscribeScheduledMessages();
    };
  }, [tableOptions]);

  const closeDetailsPanel = () => {
    setShowDetailsPanel(false);
    setDefaultFormValues(undefined);
  };

  const columns = getColumns({
    title: (item: ICampaignDetails) => <CampaignTitle campaign={item} />,
    status: (item: ICampaignDetails) => (
      <CampaignStatus
        status={item.status}
        startsAt={item.startsAt ?? new Date()}
        totalMessagesScheduled={item.totalMessagesScheduled ?? 0}
        totalMessagesSent={item.totalMessagesSent ?? 0}
        totalMessagesConfirmed={item.totalMessagesConfirmed ?? 0}
        sendingWindow={sendingWindow}
        allListsClassified={item.allListsClassified}
      />
    ),
    deliveredProgress: (item: ICampaignDetails) => <CampaignDeliveredProgressBar campaign={item} />,
    healthCheck: (item: ICampaignDetails) =>
      item.sendTestMessages ? (
        item.totalTestMessagesFailed > 0 ? (
          <span title="One or more test messages failed to deliver" className="select-none">
            <XCircleIcon className="w-5 h-5 text-red-400" />
          </span>
        ) : item.totalTestMessagesSent === item.totalTestMessagesDelivered && item.totalTestMessagesSent !== 0 ? (
          <span title="All test messages delivered" className="select-none">
            <CheckCircleIcon className="w-5 h-5 text-emerald-400" />
          </span>
        ) : (
          <span title="Not all test messages are delivered yet" className="select-none">
            <ClockIcon className="w-5 h-5 text-sky-400" />
          </span>
        )
      ) : (
        <span title="No health check performed" className="select-none">
          <MinusCircleIcon className="w-5 h-5 text-gray-400" />
        </span>
      ),
  });

  const filterColumns: IColumn[] = columns.filter((col) =>
    ['name', 'clientName', 'messageType', 'type', 'status'].includes(col.fieldName)
  );

  const handleSearch = (overrideText?: string) => {
    let searchFilter: ISearchFilter = {
      dataType: FilterDataTypeEnum.STRING,
      fieldName: 'name',
      operator: FilterOperatorEnum.CONTAINS,
      value: searchText,
    };

    if (overrideText !== null && overrideText !== undefined) {
      setSearchText(overrideText);
      searchFilter.value = overrideText;
    }

    const newSearchOptions = { ...defaultCampaignsTableOptions };

    const nonSearchFilters = tableOptions.filters.filter((filter) => filter.fieldName !== 'name');

    newSearchOptions.filters = searchFilter.value ? [...nonSearchFilters, searchFilter] : [...nonSearchFilters];

    setTableOptions(newSearchOptions);
  };

  const handleSubmit = async (formData: ICampaignForm) => {
    const campaignRequest: ICampaignCreationRequest = {
      name: formData.name,
      clientId: formData.client?.value,
      contactListIds: formData.contactLists.map((cl) => cl.value),
      suppressionListIds: formData.suppressionLists.map((sl) => sl.value),
      suppressionAreaCodes: formData.suppressionAreaCodes.map((sac) => sac.value),
      suppressionStates: formData.suppressionStates.map((ss) => ss.value),
      clickerGroupId: (formData.clickerGroup !== '' ? formData.clickerGroup : null) ?? null,
      messageType: (formData.messageType as any as IRadioButtonOption).value,
      message: formData.message,
      startsAt: formData.startsAt?.toISOString() ?? new Date().toISOString(),
      endsAt: formData.endsAt?.toISOString() ?? getCampaignEndDateForStartDate(new Date()).toISOString(),
      externalId: formData.externalId,
      mediaName: formData.mediaName ?? '',
      mediaUrl: formData.mediaUrl ?? '',
      isMMSVideo: formData.isMMSVideo ?? false,
      sendTestMessages: formData.sendTestMessages ?? false,
      type: formData.campaignType!,
      clickTrackingEnabled: formData.clickTrackingEnabled ?? false,
      generateUniqueLinks: formData.generateUniqueLinks ?? false,
      enableTwoWay: (formData as any).enableTwoWay ?? false,
      url: formData.clickTrackingEnabled ? formData.url : '',
      domain: formData.clickTrackingEnabled ? formData.domain : '',
      timezonePrioritizationEnabled: formData.timezonePrioritizationEnabled ?? true,
    };

    setCampaignCreating(true);

    // Create campaign
    try {
      await axiosPost<IHttpResponse<string>>('/campaigns-create', campaignRequest);

      setTableOptions({ ...defaultCampaignsTableOptions });

      closeDetailsPanel();

      addNotification({
        header: 'Campaign saved successfully!',
      });
    } catch (err) {
      console.error('An error occurred when attempting to create a new campaign');
    } finally {
      setCampaignCreating(false);
    }
  };

  const toggleCampaignPause = async (): Promise<void> => {
    if (!selectedItem) return;

    setActionLoading(true);

    try {
      // TODO: Use hook for loading indicator
      await axiosPatch(`v1/campaigns/${selectedItem.id}`, {
        status: isCampaignPausable(selectedItem.status) ? CampaignStatusEnum.PAUSED : CampaignStatusEnum.LIVE
      });
      // once campaign toggle status is complete remove loading spinner
      setActionLoading(false);
      setLoadingOverride(true);
      await handleRefetch();
      setLoadingOverride(false);

      addNotification({
        header:
          selectedItem.status === CampaignStatusEnum.PAUSED
            ? 'Campaign resumed successfully'
            : 'Campaign paused successfully',
      });
    } catch (err) {
      console.error(err);
    } finally {
      setSelectedItem(undefined);
      setActionLoading(false);
    }
  };

  const togglePriority = async (): Promise<void> => {
    if (!selectedItem) return;

    const nextIsPrioritized = selectedItem.priorityLevel !== 2;

    setActionLoading(true);

    try {
      await axiosPatch(`/campaigns/${selectedItem.id}`, { isPrioritized: nextIsPrioritized });

      setActionLoading(false);
      setLoadingOverride(true);
      await handleRefetch();
      setLoadingOverride(false);

      addNotification({
        type: NotificationType.SUCCESS,
        header: nextIsPrioritized ? 'Campaign prioritized' : 'Campaign de-prioritized',
      });
    } catch (err: any) {
      console.error(err);

      if (err?.response?.status === 409) {
        addNotification({
          type: NotificationType.FAILURE,
          header: 'Campaign status changed. Priority could not be updated.',
        });
        setLoadingOverride(true);
        await handleRefetch();
        setLoadingOverride(false);
      } else {
        addNotification({
          type: NotificationType.FAILURE,
          header: 'Failed to update campaign priority.',
        });
      }
    } finally {
      setSelectedItem(undefined);
      setActionLoading(false);
    }
  };

  const swapProvider = async (providerId: string): Promise<void> => {
    if (!selectedItem?.clientId || !selectedItem?.type || !providerId) return;

    const { clientId, type, clientName } = selectedItem;

    setActionLoading(true);

    // Pre-flight → confirm → commit (MES-2614). Only a failure here means the provider was NOT
    // changed, so this is the only path allowed to say so — a refetch failure below must not be
    // reported as a failed swap.
    try {
      const request: IProvidersChangeForTypeRequest = { clientId, providerId, campaignType: type };

      // A cancel wrote nothing — no notification, no cleanup beyond resetting the row state.
      const { outcome, blockedContent } = await executeProviderSwap(request, confirmSwapWarning);

      if (outcome === ProviderSwapOutcomeEnum.BLOCKED) {
        addNotification({ type: NotificationType.FAILURE, header: 'Provider not changed', content: blockedContent });
      }
      if (outcome !== ProviderSwapOutcomeEnum.COMMITTED) {
        setSelectedItem(undefined);
        setActionLoading(false);
        return;
      }
    } catch (err) {
      console.error(err);

      addNotification({
        type: NotificationType.FAILURE,
        header: 'Provider not changed',
        content: getProviderSwapErrorContent(err),
      });

      closeSwapWarningModal();
      setSelectedItem(undefined);
      setActionLoading(false);
      return;
    }

    // The swap has committed. Refresh the whole grid rather than patching the clicked row, since
    // the swap re-points every campaign of this client and type.
    closeSwapWarningModal();
    setActionLoading(false);
    setLoadingOverride(true);
    try {
      await handleRefetch();
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingOverride(false);
      setSelectedItem(undefined);
      setActionLoading(false);
    }

    addNotification({
      type: NotificationType.SUCCESS,
      header: `Provider updated for all ${getCampaignTypeLabel(type)} campaigns under ${clientName ?? 'this client'}.`,
    });
  };

  const handleCampaignClone = async (item: ICampaignsListView): Promise<void> => {
    try {
      setActionLoading(true);
      let startsAt = new Date(item?.startsAt ?? ''); // UTC -> EST
      let endsAt = new Date(item?.endsAt ?? '');
      if (startsAt < new Date()) {
        startsAt = new Date();
        startsAt.setTime(startsAt.getTime()); // set date to today; keep time the same
      }
      endsAt = item?.endsAt ? getCampaignEndDateForClone(startsAt ?? getCampaignStartDate(), endsAt) : getCampaignEndDateForStartDate(startsAt ?? getCampaignStartDate());
      const req: ICampaignCloneRequest = { campaignId: item?.id ?? '', startsAt: startsAt.toISOString(), endsAt: endsAt.toISOString() };
      const response = await axiosPost(`/campaigns/clone`, req);
      if (response.status === 200) {
        addNotification({ type: NotificationType.SUCCESS, header: 'Successfully cloned campaign.' });
        // if user navigates from campaign page during cloning, do not redirect to campaign edit page
        if (history.location.pathname === '/app/campaigns')
          history.push(`/app/campaigns/${response?.data?.data?.id}/edit`);
      } else {
        addNotification({ type: NotificationType.FAILURE, header: 'Failed to clone campaign.' });
      }
    } catch (err) {
      addNotification({ type: NotificationType.FAILURE, header: 'Failed to clone campaign.' });
      console.error(err);
    } finally {
      setActionLoading(false);
    }
  };

  const handleCampaignDelete = async (): Promise<void> => {
    if (!selectedItem) return;

    setActionLoading(true);

    const deleteRequest: IDeleteOneRequest = {
      entityId: selectedItem.id!,
      tableName: EntityName.CAMPAIGNS,
    };

    try {
      await axiosPost('/entities-delete-one', deleteRequest);
      // once delete request is processed remove loading spinner
      setActionLoading(false);
      await handleRefetch();

      closeDetailsPanel();

      addNotification({
        header: 'Campaign deleted successfully',
      });
    } catch (err) {
      console.error(err);
    } finally {
      setSelectedItem(undefined);
    }
  };

  const handleExportReplies = async (item: ICampaignsListView) => {
    try {
      const request: IExportConversationsRequest = {
        campaignId: item.id!,
        campaignName: item.name ?? '',
      };
      await axiosPost<IHttpResponse<string>>('/conversations-export', request);

      closeDetailsPanel();

      addNotification({
        header: 'An email has been sent with the exported replies.',
      });
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <>
      {/* Removed padding due to date filters height. Add back in once we have a proper filters panel */}
      <div className="flex items-center justify-between pb-2 mx-auto px-[7.5rem] min-w-[1151px]">
        <h1>Campaigns</h1>
        <div className="flex">
          <TextInput disabled={actionLoading}
            id="campaignTitleSearch"
            name="campaignTitleSearch"
            placeholder="Search"
            parentClass="w-80"
            value={searchText}
            onEnter={(e) => {
              setTableOptions((prevState) => {
                setSearchTextEnter(searchText);
                // remove old search text from prev state
                const filteredPrevStateFilters = prevState.filters.filter((filter) => filter.fieldName !== 'name');
                prevState.filters = filteredPrevStateFilters;
                return {
                  ...prevState,
                  filters: [...prevState.filters,
                  ... (searchText.length > 0 ? [
                    {
                      fieldName: 'name',
                      dataType: FilterDataTypeEnum.STRING,
                      operator: FilterOperatorEnum.CONTAINS,
                      value: searchText,
                    },
                  ] : [])],
                };
              });
            }}
            onChange={(e) => {
              if (
                e.target.value.length < 1 &&
                tableOptions.filters.find((x) => x.fieldName === 'name')?.value?.length > 0
              ) {
                setTableOptions((prevState) => {
                  const filteredPrevStateFilters = prevState.filters.filter((filter) => filter.fieldName !== 'name');
                  filteredPrevStateFilters.push({
                    fieldName: 'name',
                    dataType: FilterDataTypeEnum.STRING,
                    operator: FilterOperatorEnum.CONTAINS,
                    value: '',
                  });
                  //to fix grid not going to first page when text is cleared
                  setSearchTextEnter('');
                  prevState.filters = filteredPrevStateFilters;
                  return {
                    ...prevState,
                    filters: [
                      ...prevState.filters
                    ],
                  };
                });
              }
              setSearchText(e.target.value);
            }}
            search
          />
          <Button
            variant={ButtonVariantEnum.PRIMARY}
            className="self-end ml-4"
            onClick={() => {
              history.push('/app/campaigns/create');
            }}
            disabled={actionLoading}
          >
            CREATE CAMPAIGN
          </Button>
        </div>
      </div>
      <div className="h-5/6 w-full min-w-[1151px]">
        {actionLoading && (
          <div
            className="flex absolute z-20 w-full h-5/6 backdrop-blur-[1.5px] min-w-[1151px]"
            aria-hidden="true"
          >
            <div className="mx-auto mt-1/5">
              <LoadingIndicator size={16} />
            </div>
          </div>
        )}
        <TableList
          key={searchTextEnter}
          loading={!loadingOverride && campaignLoading}
          shimmer
          columns={columns}
          items={campaigns}
          totalCount={campaignTotalCount ?? 0}
          limit={10}
          actions={[
            {
              label: 'Duplicate',
              action: async (item) => handleCampaignClone(item),
            },
            {
              label: 'Pause',
              action: async (item) => {
                setSelectedItem(item);
                setShowPauseModal(true);
              },
              hidden: (item) => {
                return ![CampaignStatusEnum.LIVE, CampaignStatusEnum.SCHEDULING].includes(item.status);
              },
            },
            {
              label: 'Resume',
              action: async (item) => {
                setSelectedItem(item);
                setShowPauseModal(true);
              },
              hidden: (item) => item.status !== CampaignStatusEnum.PAUSED,
            },
            {
              label: 'Prioritize',
              action: async (item) => {
                setSelectedItem(item);
                setShowPriorityModal(true);
              },
              hidden: (item) =>
                !isAdmin
                || ![CampaignStatusEnum.LIVE, CampaignStatusEnum.PAUSED].includes(item.status)
                || item.priorityLevel === 2,
            },
            {
              label: 'De-prioritize',
              action: async (item) => {
                setSelectedItem(item);
                setShowPriorityModal(true);
              },
              hidden: (item) =>
                !isAdmin
                || ![CampaignStatusEnum.LIVE, CampaignStatusEnum.PAUSED].includes(item.status)
                || item.priorityLevel !== 2,
            },
            {
              label: 'Swap Provider',
              action: async (item) => {
                setSelectedItem(item);
                setShowSwapProviderModal(true);
              },
              hidden: (item) => !isAdmin || !item.clientId || !item.type,
            },
            {
              label: 'Delete',
              action: async (item) => {
                setSelectedItem(item);
                setShowDeleteModal(true);
              },
              hidden: (item) => {
                // TODO: Remove this check and create a legit delete campaign API. In this API we need to pause the campaign so that sending stops immediately
                return (
                  ![CampaignStatusEnum.PAUSED, CampaignStatusEnum.DRAFT].includes(item.status) ||
                  item.totalMessagesSent > 0
                );
              },
            },
          ]}
          skipEnumFormattingColumns={['messageType']}
          paginate
          onPaginate={(page) => {
            setTableOptions((prevState) => ({
              ...prevState,
              pagination: {
                ...prevState.pagination,
                skip: page * prevState.pagination.take,
              },
            }));
          }}
          filtering={{
            filters: [{
              label: 'Client',
              id: 'clientId',
              dataType: FilterDataTypeEnum.STRING,
              customDropdown: (value, onChange) => {
                return <ClientDropdown
                  placeholder='Search for a client'
                  gray={true}
                  noLabel={true}
                  value={value}
                  onChange={(newValue) => {
                    onChange(newValue as IDropdownValue);
                  }}
                />;
              }
            }, {
              label: 'Campaign Type',
              id: 'type',
              dataType: FilterDataTypeEnum.STRING,
              options: Object.keys(CampaignTypeEnum).map(key => {
                return {
                  label: convertEnumToReadableString(CampaignTypeEnum[key]),
                  value: CampaignTypeEnum[key]
                };
              }),
              optionsLabel: 'Select Type',
            }, {
              label: 'Message Type',
              id: 'messageType',
              dataType: FilterDataTypeEnum.STRING,
              options: Object.keys(MessageTypeEnum).map(key => {
                return {
                  label: key,
                  value: MessageTypeEnum[key]
                };
              }),
              optionsLabel: 'Select Message Type'
            }, {
              label: 'Status',
              id: 'status',
              dataType: FilterDataTypeEnum.STRING,
              options: Object.values(UiCampaignStatusEnum).map(s => ({
                label: s,
                value: s
              })),
              optionsLabel: 'Select Status'
            }],
            onAppliedFiltersChange: (filters: TableListFilter[]) => {
              if (filters.length > 0) {
                const formattedFilters: ISearchFilter[] = filters.map(f => {
                  if (f.id !== 'status') {
                    return [{
                      fieldName: f.id,
                      dataType: f.dataType as FilterDataTypeEnum,
                      operator: FilterOperatorEnum.EQUALS,
                      value: f.selectedOption?.value
                    }];
                  }

                  const complexFilter = mapToStatusFilter(f.selectedOption?.value, sendingWindow);
                  const statusFilter = {
                    fieldName: 'status',
                    dataType: f.dataType as FilterDataTypeEnum,
                    operator: FilterOperatorEnum.EQUALS,
                    value: complexFilter.value
                  };
                  return complexFilter.additionalFilters?.length ? [...complexFilter.additionalFilters, statusFilter] : [statusFilter];
                })
                  .flat();

                // todo: logic added to ensure search text filter included after changes made to filter. remove and incorporate title search in table list component
                const searchTextFilter = tableOptions.filters.find((option) => option.fieldName === 'name');
                if (searchTextFilter) formattedFilters.push(searchTextFilter);
                setTableOptions((prevState) => ({
                  ...prevState,
                  filters: [
                    ...formattedFilters
                  ]
                }));
              } else {
                // if table list filter is cleared ensure title search is not cleared as wel
                const searchTextFilter = tableOptions.filters.find((option) => option.fieldName === 'name');
                let updatedTableOptions = defaultCampaignsTableOptions;
                if (searchTextFilter) updatedTableOptions = { ...updatedTableOptions, filters: [searchTextFilter] };
                setTableOptions(updatedTableOptions);
              }
            },
          }}
        />
      </div>

      <CampaignDeleteModal
        show={showDeleteModal}
        onClose={() => {
          setSelectedItem(undefined);
          setShowDeleteModal(false);
        }}
        onSuccess={() => {
          handleCampaignDelete();
          setShowDeleteModal(false);
        }}
        onCancel={() => {
          setSelectedItem(undefined);
          setShowDeleteModal(false);
        }}
      />

      <CampaignPauseResumeModal
        isCampaignActive={isCampaignPausable(selectedItem?.status) ?? false}
        showModal={showPauseModal}
        onClose={() => {
          setSelectedItem(undefined);
          setShowPauseModal(false);
        }}
        onSuccess={() => {
          toggleCampaignPause();
          setShowPauseModal(false);
        }}
        onCancel={() => {
          setSelectedItem(undefined);
          setShowPauseModal(false);
        }}
      />

      <CampaignPriorityModal
        isPrioritized={selectedItem?.priorityLevel === 2}
        showModal={showPriorityModal}
        onClose={() => {
          setSelectedItem(undefined);
          setShowPriorityModal(false);
        }}
        onSuccess={() => {
          togglePriority();
          setShowPriorityModal(false);
        }}
        onCancel={() => {
          setSelectedItem(undefined);
          setShowPriorityModal(false);
        }}
      />

      {/* Mounted only while open — every row action sets selectedItem, and an always-mounted modal
          would let the provider dropdown fetch on Delete/Pause/Prioritize too. */}
      {showSwapProviderModal && !!selectedItem?.clientId && !!selectedItem?.type && (
        <CampaignSwapProviderModal
          showModal={showSwapProviderModal}
          clientId={selectedItem.clientId}
          clientName={selectedItem.clientName ?? 'this client'}
          campaignType={selectedItem.type}
          campaignTypeLabel={getCampaignTypeLabel(selectedItem.type)}
          onClose={() => {
            setSelectedItem(undefined);
            setShowSwapProviderModal(false);
          }}
          onSuccess={(providerId) => {
            swapProvider(providerId);
            setShowSwapProviderModal(false);
          }}
          onCancel={() => {
            setSelectedItem(undefined);
            setShowSwapProviderModal(false);
          }}
        />
      )}

      {/* Area-code warning for the grid swap (MES-2614) — Figma-minimal: no warnings list, no
          scope sentence (the swap modal's body copy and the success toast carry the scope). */}
      <ProviderSwapWarningModal
        show={showSwapWarningModal}
        confirmLabel="UPDATE PROVIDER"
        onConfirm={handleSwapWarningConfirm}
        onCancel={handleSwapWarningCancel}
      />
    </>
  );
};

export default Campaigns;
